import Currency from '@/constructs/Currency';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import { MoneyModel } from '../../../models/Money';
import { UserType } from '../../../models/User';
import CartService from '../../CartService';
import LoggerService from '../../LoggerService';
import ProductService from '../../ProductService';
import UserService from '../../UserService';
import type IAttentiveAddToCartEventPayload from './models/IAttentiveAddToCartEventPayload';
import type IAttentiveCart from './models/IAttentiveCart';
import type IAttentiveItem from './models/IAttentiveItem';
import type IAttentivePrice from './models/IAttentivePrice';
import type IAttentiveProductViewEventPayload from './models/IAttentiveProductViewEventPayload';
import type IAttentivePurchaseEventPayload from './models/IAttentivePurchaseEventPayload';
import type IAttentiveUser from './models/IAttentiveUser';
import type { ICart } from '../../../models/Cart';
import type { IOrderLine } from '../../../models/Order';
import type { IPlacedOrder } from '../../../models/Order/PlacedOrder';
import type { IProduct } from '../../../models/Product';
import type IAttentiveDataHandler from './IAttentiveDataHandler';

/**
 * Transforms internal domain models into Attentive SDK event payloads.
 *
 * This class is responsible for mapping our internal product, cart, and order
 * representations to the format expected by the Attentive Tag SDK.
 */
export default class AttentiveDataHandler implements IAttentiveDataHandler {
  /** @inheritdoc */
  public async getProductViewData(
    product: IProduct
  ): Promise<IAttentiveProductViewEventPayload> {
    const item = this.toAttentiveItem(product);
    const user = await this.getAttentiveUser();

    return {
      items: [{ ...item, price: this.getPrice(product), quantity: 1 }],
      user
    };
  }

  /** @inheritdoc */
  public async getAddToCartData(
    product: IProduct
  ): Promise<IAttentiveAddToCartEventPayload> {
    const item = this.toAttentiveItem(product);
    const user = await this.getAttentiveUser();
    const cart = await this.getAttentiveCart();

    return {
      items: [{ ...item, price: this.getPrice(product), quantity: 1 }],
      cart,
      user
    };
  }

  /** @inheritdoc */
  public async getPurchaseData(
    orderData: IPlacedOrder,
    cartData: ICart
  ): Promise<IAttentivePurchaseEventPayload> {
    const [items, user] = await Promise.all([
      Promise.all(
        orderData.lines.map(async (line) =>
          this.toAttentiveItemFromOrderLine(line)
        )
      ),
      this.getAttentiveUser()
    ]);

    return {
      items,
      order: { orderId: orderData.orderID.displayValue },
      cart: {
        cartId: cartData.uuid,
        cartCoupon: this.getPromotionCodesFromCart(cartData)
      },
      user
    };
  }

  /** @inheritdoc */
  public async getAttentiveUser(): Promise<IAttentiveUser | undefined> {
    try {
      const user = await UserService.getCurrentUser();

      if (isNullish(user)) {
        return undefined;
      }

      // If the user is anonymous, we only send the session ID as the external identifier.
      if (user.type !== UserType.Authenticated) {
        return {
          externalIdentifiers: [
            {
              name: 'deckers-session-id',
              // For guest users, the session ID is the same as the user ID.
              value: user.uuid
            }
          ]
        };
      }

      return {
        email: user.email,
        externalIdentifiers: [{ name: 'deckers-uuid', value: user.uuid }]
      };
    } catch {
      // If we fail to retrieve the user, we can return undefined as this is optional in the attentive event payload.
      return undefined;
    }
  }

  /**
   * Converts an internal product to an Attentive item.
   *
   * Reads properties directly from {@link IProduct} to avoid constructing a
   * {@link ProductModel} (which triggers inventory and config side effects).
   *
   * @param product - The internal product data, without price and quantity, that are extracted from line items.
   * @returns The Attentive item representation.
   */
  private toAttentiveItem(
    product: IProduct
  ): Omit<IAttentiveItem, 'price' | 'quantity'> {
    // Replicate ProductModel.primaryCategoryId: last category's ID, or empty string.
    const primaryCategoryId =
      product.categories?.at((product.categories?.length ?? 0) - 1)?.id ?? '';

    return {
      productId: product.styleNumber,
      productVariantId: product.sku,
      name: product.name,
      productImage: product.primaryImage.src,
      category: primaryCategoryId
    };
  }

  /**
   * Extracts an Attentive-formatted price from a product.
   *
   * Falls back to a zero price for products without pricing (e.g., gift cards)
   * because the Attentive SDK requires the price object in all event payloads.
   *
   * @param product - The product to extract pricing from.
   * @returns The Attentive price.
   */
  private getPrice(product: IProduct): IAttentivePrice {
    if (isNullish(product.price)) {
      // Attentive SDK requires a price object; default to zero for products without pricing.
      return { value: '0.00', currency: Currency.USD };
    }

    const money = MoneyModel.from({
      amount: product.price.price.amount,
      currency: product.price.price.currency
    });

    return { value: money.toFixed().amount, currency: money.currency };
  }

  /**
   * Converts an order line to an Attentive item.
   *
   * Fetches the full product via {@link ProductService.getProductDTOById}
   * using the order line's UPC so that image, category, and style data are
   * available. The product cache makes this inexpensive for repeated lookups.
   *
   * Falls back to order-line-only data if the product fetch fails.
   *
   * @param line - The order line from a placed order.
   * @returns The Attentive item representation.
   */
  private async toAttentiveItemFromOrderLine(
    line: IOrderLine
  ): Promise<IAttentiveItem> {
    const unitPrice = MoneyModel.from(line.netTotal)
      .divideBy(line.quantity)
      .toFixed();

    try {
      const product = await ProductService.getProductDTOById(line.upc);
      const item = this.toAttentiveItem(product);

      return {
        ...item,
        price: { value: unitPrice.amount, currency: line.netTotal.currency },
        quantity: line.quantity
      };
    } catch {
      LoggerService.warn(
        `Could not fetch product for UPC "${line.upc}". ` +
          'Falling back to order line data.'
      );

      const styleNumber = line.sku.split('-')[0] || line.sku;

      return {
        productId: styleNumber,
        productVariantId: line.sku,
        name: line.name,
        productImage: '',
        category: '',
        price: { value: unitPrice.amount, currency: line.netTotal.currency },
        quantity: line.quantity
      };
    }
  }

  /**
   * Retrieves cart information for Attentive events.
   *
   * @returns The Attentive cart object with ID and promotion codes, or undefined if cart unavailable.
   */
  private async getAttentiveCart(): Promise<IAttentiveCart | undefined> {
    try {
      const cart = await CartService.getCart();

      if (isNullish(cart)) {
        return undefined;
      }

      const cartCoupon = this.getPromotionCodesFromCart(cart);

      return { cartId: cart.uuid, cartCoupon };
    } catch {
      // Cart not available
      return undefined;
    }
  }

  /**
   * Extracts comma-separated promotion coupon codes from a cart.
   *
   * Filters out empty coupon codes and joins valid codes with commas.
   * Similar to GTMDataHandler.getPromotionCodesFromCart.
   *
   * @param cart - The cart data.
   * @returns Comma-separated coupon codes, or undefined if no coupons.
   */
  private getPromotionCodesFromCart(cart: ICart): string | null | undefined {
    const couponCodes = cart.coupons
      .map((coupon) => coupon.code)
      .filter((code) => !isNullOrEmpty(code) && code.trim().length > 0);

    return couponCodes.length > 0 ? couponCodes.join(',') : undefined;
  }
}
